import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ClipboardList, AlarmClock, CheckCircle2, DollarSign, ArrowRight, Plus, ShieldCheck,
  MessageSquareWarning, Zap, UserPlus, FileWarning, RadioTower, HardHat,
} from "lucide-react";
import { api } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { formatDateTime, formatMoney } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusChip } from "@/components/common/StatusChip";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const GROUPS = [
  { key: "sin_asignar", icon: UserPlus, tone: "blue" },
  { key: "vencidas", icon: AlarmClock, tone: "red" },
  { key: "esperando_aprobacion", icon: CheckCircle2, tone: "amber" },
  { key: "con_observaciones", icon: MessageSquareWarning, tone: "red" },
  { key: "con_adicionales", icon: Zap, tone: "amber" },
  { key: "cerradas_sin_facturar", icon: FileWarning, tone: "green" },
];

const Metric = ({ icon: Icon, label, value, accent, testId, onClick }) => (
  <div
    data-testid={testId}
    onClick={onClick}
    className={cn(
      "rounded-[10px] border bg-white p-5 transition-shadow hover:shadow-[0_2px_8px_rgba(15,23,42,0.06)]",
      accent ? "border-ink bg-ink" : "border-line",
      onClick && "cursor-pointer"
    )}
  >
    <div className={cn("flex items-center gap-2 text-[12px]", accent ? "text-ink-tertiary" : "text-ink-tertiary")}>
      <Icon className="h-3.5 w-3.5" strokeWidth={1.9} />
      {label}
    </div>
    <p
      className={cn(
        "mt-2.5 text-[32px] font-semibold leading-none tabular-nums tracking-[-0.02em]",
        accent ? "text-white" : "text-ink"
      )}
    >
      {value ?? "—"}
    </p>
  </div>
);

export default function HomePage() {
  const { t } = useT();
  const { brandParam } = useBrand();
  const { can } = useAuth();
  const navigate = useNavigate();

  const { data: summary, isLoading } = useQuery({
    queryKey: ["wo-summary", brandParam],
    queryFn: async () =>
      (await api.get("/work-orders/summary", { params: { brand: brandParam } })).data,
  });

  const { data: overview } = useQuery({
    queryKey: ["overview", brandParam],
    queryFn: async () => (await api.get("/overview", { params: { brand: brandParam } })).data,
  });

  const { data: tower } = useQuery({
    queryKey: ["home-tower-summary", brandParam],
    queryFn: async () =>
      (await api.get("/control-tower/summary", { params: { brand: brandParam } })).data,
    enabled: !!can("control_tower", "read"),
  });

  const { data: projectsSummary } = useQuery({
    queryKey: ["home-projects-summary", brandParam],
    queryFn: async () => (await api.get("/projects/summary", { params: { brand: brandParam } })).data,
    enabled: !!can("projects", "read"),
  });

  const grupos = summary?.grupos || {};

  return (
    <>
      <PageHeader
        title={t("wo.panel.title")}
        subtitle={t("wo.panel.subtitle")}
        testId="home-header"
        actions={
          can("work_orders", "create") && (
            <Button
              data-testid="home-new-wo"
              onClick={() => navigate("/ordenes?new=1")}
              className="h-8 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              {t("wo.new")}
            </Button>
          )
        }
      />

      <div className="flex-1 overflow-y-auto p-6" data-testid="home-page">
        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[108px] rounded-[10px]" />
            ))}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Metric icon={ClipboardList} label={t("wo.panel.open")} value={summary?.abiertas}
              testId="metric-open" />
            <Metric icon={AlarmClock} label={t("wo.panel.overdue")} value={grupos.vencidas}
              testId="metric-overdue" />
            <Metric icon={CheckCircle2} label={t("wo.panel.waiting")} value={grupos.esperando_aprobacion}
              testId="metric-waiting" />
            {summary?.monto_cerrado_sin_facturar_cents !== undefined && (
              <Metric
                icon={DollarSign}
                accent
                label={t("wo.panel.unbilled")}
                value={formatMoney(summary.monto_cerrado_sin_facturar_cents)}
                testId="metric-unbilled"
              />
            )}
            {summary?.no_liquidable_cantidad !== undefined && !!summary.no_liquidable_cantidad && (
              <Metric
                icon={FileWarning}
                label={t("wo.panel.not_liquidable")}
                value={formatMoney(summary.no_liquidable_monto_cents)}
                testId="metric-not-liquidable"
                onClick={() => navigate("/ordenes?vista=sin_poder_liquidar")}
              />
            )}
            {!!projectsSummary?.alertas && (
              <Metric
                icon={HardHat}
                label={t("projects.home.alerts")}
                value={projectsSummary.alertas}
                testId="metric-projects-alerts"
                onClick={() => navigate("/proyectos?alerta=1")}
              />
            )}
          </div>
        )}

        <section className="mt-6" data-testid="home-groups">
          <h2 className="pb-3 text-[13px] font-semibold text-ink">{t("wo.panel.groups")}</h2>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {GROUPS.map((g) => {
              const count = grupos[g.key] ?? 0;
              return (
                <button
                  key={g.key}
                  data-testid={`home-group-${g.key}`}
                  onClick={() => navigate(`/ordenes?vista=${g.key}`)}
                  className="group flex items-center gap-3 rounded-[10px] border border-line bg-white px-4 py-3.5 text-left transition-all hover:border-line-strong hover:shadow-[0_2px_8px_rgba(15,23,42,0.06)]"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] bg-line text-ink-secondary">
                    <g.icon className="h-4 w-4" strokeWidth={1.9} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13.5px] font-medium text-ink">
                      {t(`wo.views.${g.key}`)}
                    </span>
                    <span className="block text-[11.5px] text-ink-tertiary">
                      {count ? t("wo.panel.see") : t("wo.panel.all_clear")}
                    </span>
                  </span>
                  {count ? (
                    <StatusChip tone={g.tone} label={count} testId={`home-count-${g.key}`} />
                  ) : (
                    <span className="text-[13px] tabular-nums text-line-strong">0</span>
                  )}
                  <ArrowRight className="h-3.5 w-3.5 text-line-strong transition-transform group-hover:translate-x-0.5 group-hover:text-ink-tertiary" />
                </button>
              );
            })}
          </div>
        </section>

        {!!tower?.abiertas && (
          <section className="mt-6" data-testid="home-tower">
            <div className="flex items-center justify-between pb-3">
              <h2 className="text-[13px] font-semibold text-ink">{t("tower.title")}</h2>
              <button data-testid="home-go-tower" onClick={() => navigate("/torre-de-control")}
                className="inline-flex items-center gap-1.5 text-[12px] text-ink-tertiary hover:text-ink">
                <RadioTower className="h-3.5 w-3.5" /> {t("tower.go")}
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <button data-testid="home-tower-open" onClick={() => navigate("/torre-de-control")}
                className="flex items-center gap-3 rounded-[10px] border border-state-critical-soft bg-state-critical-soft px-4 py-3.5 text-left transition-shadow hover:shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
                <RadioTower className="h-4 w-4 text-state-critical" />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13.5px] font-medium text-state-critical">{t("tower.open")}</span>
                  <span className="block text-[11.5px] text-state-critical">{t("tower.subtitle")}</span>
                </span>
                <span className="text-[20px] font-semibold tabular-nums text-state-critical">{tower.abiertas}</span>
              </button>
              {Object.entries(tower.por_regla || {}).slice(0, 3).map(([k, v]) => (
                <button key={k} data-testid={`home-tower-${k}`}
                  onClick={() => navigate("/torre-de-control")}
                  className="flex items-center gap-3 rounded-[10px] border border-line bg-white px-4 py-3.5 text-left transition-shadow hover:shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
                  <span className="min-w-0 flex-1 text-[13.5px] font-medium text-ink">
                    {t(`tower.rules.${k}`)}
                  </span>
                  <span className="text-[18px] font-semibold tabular-nums text-ink">{v}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {!!overview?.actividad_reciente?.length && (
          <section className="mt-6 rounded-[10px] border border-line bg-white">
            <div className="flex items-center justify-between border-b border-line px-4 py-3">
              <h2 className="text-[13px] font-semibold text-ink">{t("home.recent_activity")}</h2>
              {can("audit", "read") && (
                <button
                  data-testid="home-go-audit"
                  onClick={() => navigate("/auditoria")}
                  className="inline-flex items-center gap-1.5 text-[12px] text-ink-tertiary hover:text-ink"
                >
                  <ShieldCheck className="h-3.5 w-3.5" /> {t("home.go_audit")}
                </button>
              )}
            </div>
            <ul data-testid="recent-activity">
              {overview.actividad_reciente.map((a) => (
                <li key={a.id} className="flex items-center gap-3 border-b border-line px-4 py-2 last:border-0">
                  <StatusChip
                    tone={
                      a.accion === "access_denied" || a.accion === "login_failed" || a.accion === "transition_rejected"
                        ? "red"
                        : "blue"
                    }
                    label={t(`audit.actions.${a.accion}`)}
                  />
                  <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
                    {a.entity_code || a.entidad}
                  </span>
                  <span className="text-[12px] text-ink-tertiary">{a.user_nombre}</span>
                  <span className="text-[12px] tabular-nums text-ink-tertiary">
                    {formatDateTime(a.timestamp)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </>
  );
}
